/* Copyright (c) 2020-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Component, Input, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, NgZone } from '@angular/core';
import {
    AnimationEvent,
    trigger,
    animate,
    transition,
    style,
    state,
    query,
    group,
    animateChild
} from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { ExtendedModule, FlexModule } from '@angular/flex-layout';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, combineLatest, fromEvent, merge } from 'rxjs';
import { takeUntil, take, filter, tap } from 'rxjs/operators';
import { LoggingService, Logger } from 'crimson/lib/logging/logging.service';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { SpatialNavigationService } from 'hig/lib/services/spatial-navigation/spatial-navigation.service';
import { FocusManager } from 'hig/lib/services/focus-manager';
import { FocusBackDirective } from 'hig/lib/directives/focus-back.directive';
import { HeadingFocusableDirective } from 'hig/lib/directives/heading-focusable.directive';
import { NvDisableTooltipDirective } from 'hig/lib/directives/nv-disable-tooltip.directive';
import { NvFocusRippleDirective } from 'hig/lib/directives/focus-ripple.directive';
import { fromKeyboardEvent, isBackKey, KeyboardEventBackKeyValue } from 'utils/lib/web-events/keyboard-event';
import { AppBarService, AppBarIcon } from '../../services/app-bar/app-bar.service';

enum AppBarLogoState {
    InInitial = 'IN_INITIAL',
    In = 'IN',
    Out = 'OUT'
}

export enum AppBarLogoBehavior {
    ShowInitialReveal = 'showInitialReveal',
    ShowWhenNoNavButton = 'showWhenNoNavButton',
    NoShow = 'noShow'
}

@Component({
    selector: 'nv-app-bar',
    templateUrl: './nv-app-bar.component.html',
    styleUrls: ['./nv-app-bar.component.scss'],
    animations: [
        trigger('logoSlide', [
            state(AppBarLogoState.InInitial, style({ transform: 'translate3d(0, 0, 0)' })),
            state(AppBarLogoState.In, style({ transform: 'translate3d(0, 0, 0)' })),
            state(AppBarLogoState.Out, style({ transform: '{{ translate3d_value }}' }), {
                params: {
                    translate3d_value: 'translate3d(calc(-101% - env(safe-area-inset-left)), 0, 0)'
                }
            }),
            transition(AppBarLogoState.InInitial + ' => ' + AppBarLogoState.Out, animate('200ms 1500ms ease')),
            transition(AppBarLogoState.In + ' => ' + AppBarLogoState.Out, animate('400ms ease')),
            transition(AppBarLogoState.Out + ' => ' + AppBarLogoState.In, animate('650ms ease'))
        ]),
        trigger('navButton', [
            state('true', style({ 'flex-basis': '36px', opacity: 1 })),
            state('false', style({ 'flex-basis': '0', 'margin-right': 0, opacity: 0 })),
            transition('true <=> false', animate(150))
        ]),
        trigger('icon', [
            transition(':enter', [style({ opacity: 0 }), animate(150, style({ opacity: 1 }))]),
            transition(':leave', [animate(150, style({ opacity: 0 }))])
        ]),
        trigger('active', [
            state('true', style({ 'background-color': '{{active_color}}' }), {
                params: { active_color: 'transparent' }
            }),
            state('false', style({ 'background-color': '{{default_color}}' }), {
                params: { default_color: 'transparent' }
            }),
            transition('true <=> false', [group([query('@*', [animateChild()]), animate(150)])])
        ])
    ],
    // Mirrored onto the host so consumers can style by transparency without reaching into the inner header.
    host: {
        '[class.app-bar-transparent]': 'transparent'
    },
    imports: [
        FlexModule,
        FocusBackDirective,
        HeadingFocusableDirective,
        NvDisableTooltipDirective,
        NvFocusRippleDirective,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        NgClass,
        ExtendedModule,
        MatMenuModule,
        TranslateModule
    ]
})
export class NvAppBarComponent implements OnInit, OnDestroy, AfterViewInit {
    private logger: Logger;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    @Input() logoBehavior: AppBarLogoBehavior;
    @Input() skipTour = false;
    @Input() ariaLabel = '';

    // static copy
    icons: AppBarIcon[] = [{}, {}, {}, {}, {}];
    title = '';
    titleParams = {};
    info = '';
    infoParams = {};
    navButton: AppBarIcon = {};
    active: boolean;
    transparent = false;
    show = true;
    shrinkNavigation = false;
    logoState: AppBarLogoState;
    template = {};

    activeColor = '';
    defaultColor = '';
    logoSlideTranslate3dValue = '';
    isNavFocused = false;
    isSidebarOpened = false;

    @ViewChild('activecolor', { static: true }) activeElement: ElementRef;
    @ViewChild('defaultcolor', { static: true }) defaultElement: ElementRef;
    @ViewChild('navButtonRef', { read: ElementRef }) navButtonRef: ElementRef;

    constructor(
        loggingService: LoggingService,
        private ngZone: NgZone,
        private appBarService: AppBarService,
        private matDialog: MatDialog,
        private localeService: LocaleService,
        private spatialNavigationService: SpatialNavigationService,
        private focusManager: FocusManager
    ) {
        this.logger = loggingService.getLogger('AppBar');
    }

    private setupListeners() {
        this.appBarService.icons$.pipe(takeUntil(this.destroy$)).subscribe(icons => {
            let i = 4;
            icons.forEach(icon => {
                if (i >= 0) {
                    this.icons[i--] = icon;
                }
            });
            while (i >= 0) {
                this.icons[i--] = {};
            }
        });

        this.appBarService.title$.pipe(takeUntil(this.destroy$)).subscribe(title => {
            this.title = title.entry;
            this.titleParams = title.params;
        });

        this.appBarService.info$.pipe(takeUntil(this.destroy$)).subscribe(info => {
            this.info = info.entry;
            this.infoParams = info.params;
        });

        this.appBarService.navButton$.pipe(takeUntil(this.destroy$)).subscribe(nav => {
            this.navButton = nav;
            if (this.logoBehavior === AppBarLogoBehavior.ShowWhenNoNavButton) {
                this.logoState = this.navButton?.id ? AppBarLogoState.Out : AppBarLogoState.In;
            }
        });

        this.appBarService.active$.pipe(takeUntil(this.destroy$)).subscribe(active => {
            this.active = active;
        });

        this.appBarService.transparent$.pipe(takeUntil(this.destroy$)).subscribe(transparent => {
            this.transparent = transparent;
        });

        this.appBarService.visible$.pipe(takeUntil(this.destroy$)).subscribe(visible => {
            this.show = visible;
        });

        this.appBarService.template$.pipe(takeUntil(this.destroy$)).subscribe(template => {
            this.template = template;
        });

        const layoutDirectionUpdate$ = this.localeService.layoutDirectionUpdated$.pipe(take(1));
        combineLatest([layoutDirectionUpdate$, this.appBarService.logoRevealed$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([_, logoRevealed]) => {
                // Move logo out from the viewport
                const transformX = this.localeService.isRTLLayoutEnabled
                    ? 'calc(101% + env(safe-area-inset-right))'
                    : 'calc(-101% - env(safe-area-inset-left))';
                this.logoSlideTranslate3dValue = `translate3d(${transformX}, 0, 0)`;
                if (logoRevealed && this.logoState === AppBarLogoState.InInitial) {
                    this.logoState = AppBarLogoState.Out;
                }
            });

        // hide title to expand search-input box to max width
        this.appBarService.shrinkNavigation$.pipe(takeUntil(this.destroy$)).subscribe(enabled => {
            this.shrinkNavigation = enabled;
        });

        this.appBarService.sidebarOpened$.pipe(takeUntil(this.destroy$)).subscribe(opened => {
            this.isSidebarOpened = opened;
        });
    }

    ngOnInit() {
        switch (this.logoBehavior) {
            case AppBarLogoBehavior.NoShow:
                this.logoState = AppBarLogoState.Out;
                break;
            case AppBarLogoBehavior.ShowInitialReveal:
                this.logoState = AppBarLogoState.InInitial;
                break;
            case AppBarLogoBehavior.ShowWhenNoNavButton:
                this.logoState = AppBarLogoState.In;
                break;
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.activeColor = getComputedStyle(this.activeElement.nativeElement).backgroundColor;
            this.defaultColor = getComputedStyle(this.defaultElement.nativeElement).backgroundColor;
            this.setupListeners();
        }, 0);

        // document can receive every keydown event, runOutsideAngular to avoid excessive change detection
        this.ngZone.runOutsideAngular(() => {
            const backKey$ = fromEvent(document, 'keydown').pipe(filter(isBackKey));
            const escape$ = backKey$.pipe(filter(event => event.key == KeyboardEventBackKeyValue.Escape));

            const shouldProcessBack = (e: KeyboardEvent) => {
                return !e.defaultPrevented && this.navButton?.id && this.matDialog.openDialogs.length === 0;
            };

            // Focus back
            escape$.pipe(filter(shouldProcessBack), takeUntil(this.destroy$)).subscribe(event => {
                // If navButton is not focused, move the focus to navButton
                if (event.target !== this.navButtonRef.nativeElement) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.focusManager.focusViaLastOrigin(this.navButtonRef.nativeElement);
                    this.logger.info('focus navigation button');
                }
            });

            // Page back
            backKey$.pipe(filter(shouldProcessBack), takeUntil(this.destroy$)).subscribe(event => {
                if (this.navButton?.id !== 'menu') {
                    event.preventDefault();
                    event.stopPropagation();
                    this.logger.info(
                        'focus is on the navigation button and it is not menu button. clicking the navigation button.'
                    );
                    this.appBarService.clickIcon(this.navButton.id);
                }
            });
        });

        this.appBarService.focusMenuButton$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.focusManager.focusViaLastOrigin(this.navButtonRef?.nativeElement);
        });
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    navClicked(id: string) {
        this.appBarService.clickNav(id);
    }

    navFocused() {
        setTimeout(() => {
            this.isNavFocused = true;
        });
    }

    navBlurred() {
        setTimeout(() => {
            this.isNavFocused = false;
        });
    }

    iconClicked(icon: AppBarIcon) {
        this.appBarService.clickIcon(icon.id);
    }

    iconMenuClicked(id: string) {
        this.appBarService.clickIcon(id);
    }

    iconTrack(index: number, item: AppBarIcon) {
        return index;
    }

    onLogoSlide(event: AnimationEvent) {
        if (event?.fromState === AppBarLogoState.InInitial && event?.toState === AppBarLogoState.Out) {
            this.logger.info('Initial logo reveal complete');
            // Activating spatial navigation before the tour starts to facilitate product tour.
            if (this.spatialNavigationService.enabled && !this.spatialNavigationService.activated) {
                this.spatialNavigationService.activated = true;
            }
            if (this.spatialNavigationService.activated) {
                if (!this.navButtonRef?.nativeElement) {
                    this.logger.error(
                        'Unable to focus on menu, navButtonRef.nativeElement is undefined, current navButton:',
                        JSON.stringify(this.navButton)
                    );
                    return;
                }

                // only set the starting point/initial focus to the nav button if the focus hasn't been moved to elsewhere
                // as it might be moved to the CTA button in game details in the case of re-entering the mall
                // from streaming
                if (document.activeElement !== document.body) {
                    return;
                }

                // Set menu button as spatial navigation focus candidate at entering mall
                if (this.spatialNavigationService.isDefaultFocusRequired()) {
                    this.focusManager.focusViaLastOrigin(this.navButtonRef.nativeElement);
                } else {
                    this.spatialNavigationService.setStartingPoint(this.navButtonRef.nativeElement);
                }
            }
        }
    }
}
